import { Card } from '@/components/ui/card';
import { 
    Phone, 
    Calendar,
    Globe,
    Users,
    User,
    Webhook
} from 'lucide-react';
import { motion } from 'framer-motion';
import type { PhoneNumberDetails } from '@/api/phone-numbers/phone_numbers.types';
import { FadeId } from '@/components/ui/fade-id';

interface PhoneNumberCardProps {
    phoneNumber: PhoneNumberDetails;
    onClick: () => void;
    variants?: any;
}

// StatButton Component - Same as AssistantCard
const StatButton = ({ 
    icon: Icon, 
    value, 
    label, 
    tooltip,
    active = false
}: { 
    icon: any; 
    value: string | number; 
    label: string; 
    tooltip?: string;
    active?: boolean;
}) => {
    return (
        <div 
            className={`h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2 hover:bg-muted/50 transition-colors cursor-default ${
                active ? 'bg-primary/5' : ''
            }`}
            title={tooltip}
        >
            <div className="flex items-center gap-1.5 mb-0.5">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold truncate max-w-[80px]">{value}</span>
            </div>
            <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">{label}</span>
        </div>
    );
};

export const PhoneNumberCard = ({ phoneNumber, onClick, variants }: PhoneNumberCardProps) => {
    // Provider display
    const providerDisplay = phoneNumber.provider === 'byo-phone-number' ? 'SIP' : phoneNumber.provider;
    
    // Assistant status
    const hasAssistant = phoneNumber.assistantId ? 'Yes' : 'No';
    
    // Multi-Agent status
    const hasMultiAgent = phoneNumber.squadId ? 'Yes' : 'No';
    
    // Webhook status
    const hasWebhook = phoneNumber.serverUrl ? 'Yes' : 'No';

    return (
        <motion.div variants={variants}>
            <Card
                className="group relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
                onClick={onClick}
            >
                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* Main Content */}
                <div className="relative p-5 space-y-4">
                    {/* Header with Icon, Title, and ID */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Icon */}
                            <div className="relative flex-shrink-0">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm">
                                    <Phone className="h-5 w-5 text-primary" />
                                </div>
                            </div>

                            {/* Title and Date */}
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-semibold tracking-tight truncate">
                                    {phoneNumber.name}
                                </h3>
                                {/* Date */}
                                {phoneNumber.updatedAt && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(phoneNumber.updatedAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Phone Number ID on Right */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <FadeId 
                                id={phoneNumber.id} 
                                label="Phone Number ID"
                                tooltipSide="top"
                            />
                        </div>
                    </div>

                    {/* Unified Configuration Grid with Dividers */}
                    <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Row 1 - Phone Number (2 cells) | Provider (1 cell) */}
                            
                            {/* Phone Number - Spans 2 columns */}
                            <div 
                                className="col-span-2 h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2 hover:bg-muted/50 transition-colors cursor-default"
                                title={`Phone: ${phoneNumber.number}`}
                            >
                                <div className="flex items-center gap-1.5 mb-0.5">
                                    <Phone className="h-3.5 w-3.5 text-primary" />
                                    <span className="text-xs font-semibold font-mono truncate max-w-[180px]">{phoneNumber.number}</span>
                                </div>
                                <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">Phone Number</span>
                            </div>

                            <StatButton
                                icon={Globe}
                                value={providerDisplay}
                                label="Provider"
                                tooltip={`Provider: ${providerDisplay}`}
                            />
                        </div>
                        
                        {/* Horizontal Divider */}
                        <div className="border-t border-border" />
                        
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Row 2 - Assistant, Multi-Agent, Webhook */}
                            <StatButton
                                icon={User}
                                value={hasAssistant}
                                label="Assistant"
                                tooltip={hasAssistant === 'Yes' ? 'Assistant assigned' : 'No assistant'}
                                active={phoneNumber.assistantId !== undefined && phoneNumber.assistantId !== null && phoneNumber.assistantId !== ''}
                            />

                            <StatButton
                                icon={Users}
                                value={hasMultiAgent}
                                label="Multi-Agent"
                                tooltip={hasMultiAgent === 'Yes' ? 'Multi-agent configured' : 'No multi-agent'}
                                active={phoneNumber.squadId !== undefined && phoneNumber.squadId !== null && phoneNumber.squadId !== ''}
                            />

                            <StatButton
                                icon={Webhook}
                                value={hasWebhook}
                                label="Webhook"
                                tooltip={hasWebhook === 'Yes' ? 'Webhook URL configured' : 'No webhook'}
                                active={phoneNumber.serverUrl !== undefined && phoneNumber.serverUrl !== null && phoneNumber.serverUrl !== ''}
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};

